import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, apiClient } from '../../auth-lib';
import EmployeeLayout from '../components/EmployeeLayout';
import { Button, StatusPill, Table, EmptyState, useToast } from '../../design-system';
import { dueAging, statusLabel } from '../../lib/taskStatus';
import { useLiveRefresh } from '../../lib/useLiveRefresh';
import '../styles/Employee.css';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'todo', label: 'To Do' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'review', label: 'In Review' },
  { value: 'waiting-client', label: 'Waiting on Client' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
];

// Populated refs arrive as objects, raw ones as strings — compare either way.
const sameId = (ref, id) => {
  if (!ref || !id) return false;
  const refId = typeof ref === 'object' ? ref._id : ref;
  return String(refId) === String(id);
};

// Why this task is "mine" — mirrors involvementQuery() in
// src/utils/taskPeople.js, which is what getMyTasks now selects on. Assistant
// was missing from both sides until those were made one definition, so an
// assistant's work appeared on nobody's list at all.
// A task's client sits on `clientId` (single) or in `clientIds[]` (bulk and
// compliance-generated ones). Reading only the first would leave the column
// blank on a large share of rows. Mirrors MetaSection on the org-admin task
// dashboard and the employee task page.
const clientOf = (task) => {
  const list = task.clientId ? [task.clientId] : (task.clientIds || []);
  const first = list[0];
  if (!first) return null;
  return { name: first.displayName || '—', extra: list.length - 1 };
};

const myRoleOn = (task, userId) => {
  const roles = [];
  if (sameId(task.preparerId, userId)) roles.push('Preparer');
  if (sameId(task.reviewerId, userId)) roles.push('Reviewer');
  if ((task.assistantIds || []).some((a) => sameId(a, userId))) roles.push('Assistant');
  if (sameId(task.createdBy, userId)) roles.push('Creator');
  return roles.join(', ') || '—';
};

function EmployeeTasks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const revision = useLiveRefresh();
  // ?q= comes from the top-bar search in EmployeeLayout.
  const query = (searchParams.get('q') || '').trim().toLowerCase();

  // `revision` ticks when a notification arrives, when the socket reconnects,
  // and when the tab regains focus — so a task assigned while this list is open
  // shows up without anyone pressing reload. See lib/useLiveRefresh.
  useEffect(() => {
    apiClient
      .get('/api/tasks/my-tasks')
      .then((res) => setTasks(res.data))
      .catch((err) => toast.danger(err.response?.data?.error || 'Failed to load tasks'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  const visible = useMemo(() => {
    let list = filter === 'all' ? tasks : tasks.filter((t) => t.status === filter);
    if (query) {
      list = list.filter(
        (t) =>
          t.title?.toLowerCase().includes(query) ||
          (t.taskNumber != null && `task-${String(t.taskNumber).padStart(4, '0')}`.includes(query))
      );
    }
    return list;
  }, [tasks, filter, query]);

  const columns = [
    {
      key: 'taskNumber',
      header: '#',
      render: (t) => (t.taskNumber != null ? `TASK-${String(t.taskNumber).padStart(4, '0')}` : '-'),
    },
    { key: 'title', header: 'Task', render: (t) => t.title },
    // Which client the work is for — the first thing anyone scanning their own
    // list needs, and the column this table went without.
    {
      key: 'client',
      header: 'Client',
      render: (t) => {
        const c = clientOf(t);
        if (!c) return <span className="employee-muted">—</span>;
        return (
          <span title={c.name}>
            {c.name}
            {c.extra > 0 && <span className="employee-due-age"> +{c.extra}</span>}
          </span>
        );
      },
    },
    { key: 'myRole', header: 'My Role', render: (t) => myRoleOn(t, user?._id) },
    { key: 'priority', header: 'Priority', render: (t) => <StatusPill status={t.priority} /> },
    {
      key: 'status',
      header: 'Status',
      render: (t) => <StatusPill status={t.status} label={statusLabel(t.status)} />,
    },
    {
      key: 'dueDate',
      header: 'Due',
      // Date plus how it sits against today, so the reader isn't doing the
      // arithmetic on every row.
      render: (t) => {
        if (!t.dueDate) return '—';
        const age = dueAging(t);
        return (
          <span className={age.tone === 'overdue' ? 'employee-due-overdue' : undefined}>
            {new Date(t.dueDate).toLocaleDateString()}
            <span className="employee-due-age"> · {age.label}</span>
          </span>
        );
      },
    },
  ];

  return (
    <EmployeeLayout title="My Tasks" subtitle="Everything assigned to, reviewed by, or created by you">
      <div className="employee-page">
        <div className="employee-toolbar">
          <div className="employee-filter-row">
            {STATUS_FILTERS.map((f) => (
              <Button
                key={f.value}
                variant={filter === f.value ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </Button>
            ))}
          </div>
          <span className="employee-count">
            {visible.length} tasks
            {query && (
              <>
                {' '}for “{searchParams.get('q')}”{' '}
                <button className="employee-action-link" onClick={() => setSearchParams({})}>
                  Clear
                </button>
              </>
            )}
          </span>
        </div>

        {!loading && visible.length === 0 ? (
          <EmptyState
            icon="📭"
            title={filter === 'all' ? 'No tasks yet' : 'No tasks with this status'}
            description="Tasks where you are preparer, reviewer, assignee or creator show up here."
          />
        ) : (
          <Table
            columns={columns}
            data={visible}
            loading={loading}
            onRowClick={(t) => navigate(`/tasks/${t._id}`)}
          />
        )}
      </div>
    </EmployeeLayout>
  );
}

export default EmployeeTasks;
